    import { projectAPI } from './api';

export interface CreateProjectPayload {
  name: string; // Title
  description: string; // Body
  firm_id: number;
  // Settings and custom fields will be sent alongside or nested
  billing_type: 'Page Based' | 'Case Based' | 'Time Based' | 'Flat Rate';
  rate: number;
  // Flat Rate specific fields
  flat_rate?: number;
  flat_rate_page_from?: number;
  flat_rate_page_to?: number;
  variable_rate?: number;
  variable_rate_page_from?: number;
  variable_rate_page_to?: number;
  additional_claimant?: number;
  free_cases_allowed: number;
  hide_case_timer: boolean;
  enable_checklist: boolean;
  checklist_instructions?: string;
  checklist_files?: File[];
  // Estimate settings
  enable_estimate_approval: boolean;
  max_estimate_hours?: number;
  enable_sample_estimate: boolean;
  max_capacity_pages_per_hour?: number;
  remind_on_file_missing: boolean;
  // Disposition
  enable_disposition: boolean;
  mandatory_disposition_field?: boolean;
  disposition_list?: string[];
  // Body format selections
  body_format: 'WYSIWYG' | 'HTML' | 'Full HTML' | 'Plain text';
  checklist_instructions_format?: 'WYSIWYG' | 'HTML' | 'Full HTML' | 'Plain text';
  // Status
  status?: 'draft' | 'active';
}

export const projectService = {
  createProject: async (data: CreateProjectPayload): Promise<any> => {
    try {
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('description', data.description);
      formData.append('firm_id', String(data.firm_id));
      formData.append('billing_type', data.billing_type);
      formData.append('rate', String(data.rate));
      // Flat Rate fields
      if (data.flat_rate !== undefined) formData.append('flat_rate', String(data.flat_rate));
      if (data.flat_rate_page_from !== undefined) formData.append('flat_rate_page_from', String(data.flat_rate_page_from));
      if (data.flat_rate_page_to !== undefined) formData.append('flat_rate_page_to', String(data.flat_rate_page_to));
      if (data.variable_rate !== undefined) formData.append('variable_rate', String(data.variable_rate));
      if (data.variable_rate_page_from !== undefined) formData.append('variable_rate_page_from', String(data.variable_rate_page_from));
      if (data.variable_rate_page_to !== undefined) formData.append('variable_rate_page_to', String(data.variable_rate_page_to));
      if (data.additional_claimant !== undefined) formData.append('additional_claimant', String(data.additional_claimant));
      formData.append('free_cases_allowed', String(data.free_cases_allowed));
      // Send booleans as 1/0 to satisfy backend boolean validation
      formData.append('hide_case_timer', data.hide_case_timer ? '1' : '0');
      formData.append('enable_checklist', data.enable_checklist ? '1' : '0');
      if (data.checklist_instructions) formData.append('checklist_instructions', data.checklist_instructions);
      if (data.body_format) formData.append('body_format', data.body_format);
      if (data.checklist_instructions_format) formData.append('checklist_instructions_format', data.checklist_instructions_format);

      // Estimate
      formData.append('enable_estimate_approval', data.enable_estimate_approval ? '1' : '0');
      if (data.max_estimate_hours !== undefined) formData.append('max_estimate_hours', String(data.max_estimate_hours));
      formData.append('enable_sample_estimate', data.enable_sample_estimate ? '1' : '0');
      if (data.max_capacity_pages_per_hour !== undefined) formData.append('max_capacity_pages_per_hour', String(data.max_capacity_pages_per_hour));
      formData.append('remind_on_file_missing', data.remind_on_file_missing ? '1' : '0');

      // Disposition
      formData.append('enable_disposition', data.enable_disposition ? '1' : '0');
      if (data.mandatory_disposition_field !== undefined) formData.append('mandatory_disposition_field', data.mandatory_disposition_field ? '1' : '0');
      if (data.disposition_list && data.disposition_list.length) {
        data.disposition_list.forEach((item, idx) => formData.append(`disposition_list[${idx}]`, item));
      }

      // Status
      if (data.status) formData.append('status', data.status);

      // Files
      if (data.checklist_files && data.checklist_files.length) {
        data.checklist_files.forEach((file) => formData.append('checklist_files[]', file));
      }

      const response = await projectAPI.create(formData);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to create project');
    }
  },
};
